import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { baseApi } from '@shared/api/baseApi.js';
import { selectCurrentLanguage, selectIsAuthenticated } from '@shared/api/reduxToolkit';
import { userApi, usePatchCurrentUserMutation } from '@shared/api/rtkQuery';
import {
	DEFAULT_LANGUAGE,
	i18TranslationNS,
	languageMap,
	SUPPORTED_LANGUAGES,
	toPlatformLanguage,
	Translation,
} from '@shared/config/i18n';
import { useActions } from '@shared/lib/hooks';
import { showCustomToast, TOAST_TYPES } from '@shared/ui/toast';

export const useLanguageSwitcher = () => {
	const { i18n } = useTranslation();
	const { setLanguage } = useActions();
	const dispatch = useDispatch();
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const [patchCurrentUser] = usePatchCurrentUserMutation();
	const latestCodeRef = useRef(null);
	const saveQueueRef = useRef(Promise.resolve());

	const currentCode =
		useSelector(selectCurrentLanguage) || i18n.resolvedLanguage || DEFAULT_LANGUAGE;

	const currentLanguage = useMemo(
		() => languageMap[currentCode] || languageMap[DEFAULT_LANGUAGE],
		[currentCode],
	);

	const allLanguages = useMemo(
		() => SUPPORTED_LANGUAGES.map((code) => ({ code, ...languageMap[code] })),
		[],
	);

	// Stores the pick on the account so card-service, DiPocket and cidaas (consent pages,
	// Apple Wallet terms) use it too. The cached account language is patched first so
	// useAccountLanguage sees a match and doesn't switch the portal back while this is in flight.
	// Saves run one at a time (see selectLanguage) and a pick superseded while waiting is skipped.
	// A save superseded while in flight leaves rollback and refetch to the newer one: its undo()
	// or a refetch of its result would otherwise land on top of the newer pick's patch.
	const saveAccountLanguage = useCallback(
		async (code) => {
			if (latestCodeRef.current !== code) return;
			const language = toPlatformLanguage(code);
			const patchResult = dispatch(
				userApi.util.updateQueryData('getCurrentUser', undefined, (draft) => {
					draft.language = language;
				}),
			);
			try {
				await patchCurrentUser({ language }).unwrap();
			} catch {
				if (latestCodeRef.current === code) {
					patchResult.undo();
					showCustomToast(TOAST_TYPES.ERROR, {
						key: Translation.LANGUAGESWITCHER_SAVE_ERROR,
						ns: i18TranslationNS,
					});
				}
			} finally {
				if (latestCodeRef.current === code) dispatch(baseApi.util.invalidateTags(['User']));
			}
		},
		[dispatch, patchCurrentUser],
	);

	const selectLanguage = useCallback(
		(code) => {
			setLanguage(code);
			i18n.changeLanguage(code);
			if (!isAuthenticated) return;
			latestCodeRef.current = code;
			// saveAccountLanguage catches its own errors, so the chain never rejects.
			saveQueueRef.current = saveQueueRef.current.then(() => saveAccountLanguage(code));
		},
		[i18n, isAuthenticated, saveAccountLanguage, setLanguage],
	);

	return { currentLanguage, currentCode, allLanguages, selectLanguage };
};
